<script setup lang="ts">
import { ref, watch } from 'vue';

import { AppRawButton, AppSvgIcon } from '@libs/ui';

interface SearchThisAreaButtonProps {
  visible: boolean;
}

const props = defineProps<SearchThisAreaButtonProps>();

interface SearchThisAreaButtonEmits {
  (eventName: 'click'): void;
}

const emit = defineEmits<SearchThisAreaButtonEmits>();

const pulseActive = ref<boolean>(false);

const pulseAfterMs = 2000;
const pulseDurationMs = 700;

watch(
  () => props.visible,
  (newVal) => {
    if (newVal) {
      setTimeout(() => {
        pulseActive.value = true;
        setTimeout(() => {
          pulseActive.value = false;
        }, pulseDurationMs);
      }, pulseAfterMs);
    }
  }
);
</script>

<template>
  <transition name="search-this-area-button-transition" appear>
    <AppRawButton
      v-if="props.visible"
      class="search-this-area-button"
      :class="{ pulse: pulseActive }"
      @click="emit('click')"
    >
      <AppSvgIcon name="lookingGlass" size="18" color="color-white" />
      {{ $t('planner.search_this_area') }}
    </AppRawButton>
  </transition>
</template>

<style scoped lang="scss">
.search-this-area-button {
  @include font-label-s-bold;
  background: var(--color-brand-pop);
  color: var(--color-white);
  border-radius: var(--border-radius-12);
  box-shadow: var(--shadow-elevation-20);
  cursor: pointer;
  padding: 0 var(--spacing-12);
  height: 36px;

  display: flex;
  align-items: center;
  gap: var(--spacing-8);

  transition: all var(--transition-medium);

  &:active {
    transform: matrix(0.95, 0, 0, 0.95, 0, 0);
  }

  @include on-hover {
    background: var(--color-brand-pop-hover);
  }
}

.search-this-area-button-transition-enter-active,
.search-this-area-button-transition-leave-active {
  transition: all var(--transition-medium);
}

.search-this-area-button-transition-enter-from,
.search-this-area-button-transition-leave-to {
  opacity: 0;
  transform: scale(0.9);
}

.search-this-area-button-transition-enter-to,
.search-this-area-button-transition-leave-from {
  opacity: 1;
  transform: scale(1);
}

@keyframes search-this-area-button-pulse {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.06);
  }
}

.search-this-area-button.pulse {
  animation: search-this-area-button-pulse 0.6s ease-in-out;
}
</style>
